/**
 * Controls for the fixed half of a profile.
 *
 * The dynamic questions all render through ScaleSlider; these are the handful
 * of one-off fields that can't be a slider because they aren't opinions.
 */

import { Check, Search, X } from "lucide-react";
import { useMemo, useState } from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  AGE_CEILING,
  AGE_FLOOR,
  COUNTRIES,
  LANGUAGES,
  type Choice,
} from "@/shared/basics";

// ---------------------------------------------------------------------------

export function Field({
  label,
  help,
  filter,
  required,
  missing,
  children,
}: {
  label: string;
  help?: string;
  /** Marks a field the matcher treats as an absolute gate. */
  filter?: boolean;
  required?: boolean;
  missing?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-border/50 py-5 first:pt-0 last:border-0 last:pb-0">
      <div className="mb-2.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[15px] font-medium leading-snug">{label}</span>
          {filter && (
            <span className="rounded-full border border-brand/25 bg-brand/10 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-wide text-brand">
              hard filter
            </span>
          )}
          {required && missing && (
            <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-wide text-destructive">
              needed
            </span>
          )}
        </div>
        {help && (
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{help}</p>
        )}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function Chip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        // min-h-10 rather than padding alone: a chip whose label wraps to one
        // short word still has to be as easy to hit as one that doesn't.
        "inline-flex min-h-10 items-center rounded-full border px-4 py-2 text-[13.5px] leading-snug transition-all",
        "hover:-translate-y-px active:translate-y-0",
        selected
          ? "border-brand bg-brand text-primary-foreground shadow-sm shadow-brand/25"
          : "border-border bg-card/60 text-foreground/80 hover:border-brand/45 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

const label = (c: Choice) => (c.emoji ? `${c.emoji} ${c.label}` : c.label);

export function SingleChips({
  options,
  value,
  onChange,
}: {
  options: Choice[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <Chip
          key={o.id}
          selected={value === o.id}
          onClick={() => onChange(value === o.id ? null : o.id)}
        >
          {label(o)}
        </Chip>
      ))}
    </div>
  );
}

export function MultiChips({
  options,
  value,
  onChange,
}: {
  options: Choice[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const selected = value.includes(o.id);
        return (
          <Chip
            key={o.id}
            selected={selected}
            onClick={() =>
              onChange(selected ? value.filter((v) => v !== o.id) : [...value, o.id])
            }
          >
            {label(o)}
          </Chip>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A native <select> rather than a custom listbox: ~190 options, and the
 * platform already gives keyboard type-ahead and a usable mobile picker.
 *
 * The flag trails the name because type-ahead matches from the start of the
 * option's text — with the emoji first, typing a country's name selects
 * nothing.
 */
export function CountrySelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className={cn(
        "h-11 w-full max-w-sm rounded-md border border-input bg-card/60 px-3 text-[14px]",
        "outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        !value && "text-muted-foreground",
      )}
    >
      <option value="">Select a country…</option>
      {COUNTRIES.map((c) => (
        <option key={c.code} value={c.code}>
          {c.name}  {c.flag}
        </option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------

/**
 * Languages: chips for what's chosen, a filter box for everything else.
 * Seventy options is too many to show at once but too few to justify a
 * combobox library.
 */
export function LanguagePicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [query, setQuery] = useState("");

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = LANGUAGES.filter((l) => !value.includes(l.id));
    if (!q) return pool.slice(0, 10);
    return pool.filter((l) => l.label.toLowerCase().includes(q)).slice(0, 12);
  }, [query, value]);

  return (
    <div>
      {value.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {value.map((code) => {
            const l = LANGUAGES.find((x) => x.id === code);
            return (
              <button
                key={code}
                type="button"
                onClick={() => onChange(value.filter((v) => v !== code))}
                className="group inline-flex min-h-10 items-center gap-2 rounded-full border border-brand bg-brand py-2 pl-4 pr-3 text-[13.5px] text-primary-foreground transition-opacity hover:opacity-85"
              >
                {l?.label ?? code}
                <X className="size-4 opacity-70 group-hover:opacity-100" />
              </button>
            );
          })}
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search languages…"
          className="h-11 bg-card/60 pl-9"
        />
      </div>

      <div className="mt-2.5 flex flex-wrap gap-2">
        {matches.map((l) => (
          <Chip
            key={l.id}
            selected={false}
            onClick={() => {
              onChange([...value, l.id]);
              setQuery("");
            }}
          >
            {l.label}
          </Chip>
        ))}
        {matches.length === 0 && (
          <span className="text-[13px] text-muted-foreground">Nothing matches that.</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function AgeRange({
  min,
  max,
  onChange,
}: {
  min: number;
  max: number;
  onChange: (v: { ageMin: number; ageMax: number }) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        from
        <Input
          type="number"
          min={AGE_FLOOR}
          max={AGE_CEILING}
          value={min}
          onChange={(e) => onChange({ ageMin: Number(e.target.value), ageMax: max })}
          className="h-11 w-20 bg-card/60"
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        to
        <Input
          type="number"
          min={AGE_FLOOR}
          max={AGE_CEILING}
          value={max}
          onChange={(e) => onChange({ ageMin: min, ageMax: Number(e.target.value) })}
          className="h-11 w-20 bg-card/60"
        />
      </label>
      <span className="text-sm text-muted-foreground">years old</span>
      {min > max && (
        <span className="text-[13px] text-destructive">
          That range is inverted — it'll be swapped on save.
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function CharCount({ value, max }: { value: string; max: number }) {
  const over = value.length > max * 0.9;
  return (
    <span
      className={cn(
        "text-[11.5px] tabular-nums",
        over ? "text-brand" : "text-muted-foreground/70",
      )}
    >
      {value.length}/{max}
    </span>
  );
}

export function Saved({ show }: { show: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-[12.5px] text-brand transition-opacity",
        show ? "opacity-100" : "opacity-0",
      )}
    >
      <Check className="size-3.5" /> Saved
    </span>
  );
}
